import {
  ICalloutContentStyles,
  IContextualMenuStyles,
  IIconStyles,
  ILabelStyles,
  IModalStyles,
  IRawStyle,
  IToggleStyles,
} from '@fluentui/react';

import { IH2OTheme } from '../models/components.models';
import { defaultPalette, defaultSemanticColors } from './colors';
import { BoxShadow, FontSizes, FontWeights, Sizes } from './styles';

export const FontFamily = 'Inter';

/** ************ UI Theme Styles ***************
`default` theme will be applied by the Fluent ThemeProvider.
*/

export const contextualMenuStylesDefault = (theme: IH2OTheme): Partial<IContextualMenuStyles> => ({
  root: {
    padding: 0,
    '.ms-ContextualMenu-link': {
      padding: '0 12px',
      lineHeight: `${Sizes.height}px`,
      height: `${Sizes.height}px`,
    },
    '.ms-ContextualMenu-link:hover': {
      backgroundColor: `${theme.semanticColors?.inputListItemHoveredBackground}`,
    },
    '.ms-ContextualMenu-linkContent': {
      padding: 0,
    },
  },
});

export const contextualMenuStylesButton: Partial<IContextualMenuStyles> = {
  root: {
    paddingTop: 8,
    paddingBottom: 8,
  },
  subComponentStyles: {
    menuItem: {
      linkContent: {
        paddingLeft: 4,
      },
    },
    callout: {},
  },
};

export const iconStylesOption = (theme: IH2OTheme): Partial<IIconStyles> => ({
  root: {
    color: theme.semanticColors?.inputReadOnlyText,
    fontSize: FontSizes.optionIcon,
  },
});

export const iconStylesCaretDown = (theme: IH2OTheme): Partial<IIconStyles> => ({
  root: {
    color: theme.semanticColors?.inputReadOnlyText,
    fontSize: FontSizes.caretDown,
  },
});
export const iconStylesDarkText = (theme: IH2OTheme): Partial<IIconStyles> => ({
  root: {
    color: theme.semanticColors?.textQuaternary,
  },
});
export const iconStylesInfo = (theme: IH2OTheme): Partial<IIconStyles> => ({
  root: {
    color: theme.palette?.gray400,
    fontSize: FontSizes.medium,
  },
});
export const labelStylesDefault: Partial<ILabelStyles> = {
  root: {
    fontWeight: FontWeights.normal,
  },
};

export const modalStylesDefault: Partial<IModalStyles> = {
  root: {
    '.modal-body': {
      padding: '15px',
    },
  },
};

export const calloutContentStylesDefault = (theme: IH2OTheme): Partial<ICalloutContentStyles> => ({
  root: {
    border: `1px solid ${theme.semanticColors?.inputBorder}`,
    overflow: 'hidden',
    boxShadow: BoxShadow,
    borderRadius: Sizes.borderRadius,
    padding: `${Sizes.padding}px 0`,
    backgroundColor: theme.palette?.white,
    marginTop: 2,
  },
  calloutMain: {
    borderRadius: 0,
  },
  beakCurtain: {
    borderRadius: Sizes.borderRadius,
  },
});

export const calloutContentStylesPopover: Partial<ICalloutContentStyles> = {
  root: {
    overflow: 'visible',
    padding: 16,
  },
};

export const calloutContentStylesNoPadding: Partial<ICalloutContentStyles> = {
  root: {
    padding: 0,
  },
};

export const calloutContentStylesNoBorder: Partial<ICalloutContentStyles> = {
  root: {
    border: 0,
    overflow: 'visible',
    padding: 0,
  },
};

export const calloutContentStylesTooltip = (theme: IH2OTheme): Partial<ICalloutContentStyles> => ({
  root: {
    border: 0,
    padding: 5,
    color: theme.palette?.white,
  },
  beak: {
    backgroundColor: theme.palette?.gray800,
  },
  beakCurtain: {
    backgroundColor: theme.palette?.gray800,
  },
  calloutMain: {
    backgroundColor: theme.palette?.gray800,
    '.ms-Tooltip-content': {
      color: theme.palette?.white,
    },
  },
});

export const toggleStylesDefault = (theme: IH2OTheme): Partial<IToggleStyles> => ({
  root: { marginBottom: 0 },
  label: {
    order: 0,
    marginLeft: 0,
    marginRight: 8,
  },
  pill: {
    width: 34,
    height: 16,
    padding: 0,
    border: 0,
    backgroundColor: theme.palette?.gray300,
    '&[aria-checked="true"]': {
      backgroundColor: theme.palette?.primary500,
      '.ms-Toggle-thumb': {
        backgroundColor: theme.palette?.primary800,
      },
    },
  },
  thumb: {
    width: 16,
    height: 16,
    border: 0,
    backgroundColor: theme.palette?.gray500,
  },
});

export const toggleStylesEditor: Partial<IToggleStyles> = {
  root: {
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
  },
  container: {
    justifyContent: 'center',
  },
  pill: {
    '&:focus::after': {
      outline: '#0967D2 solid 1px !important',
    },
  },
};

export const toggleStylesLabelOnly: Partial<IToggleStyles> = {
  label: { padding: 0 },
  pill: {
    display: 'none',
  },
};

export const toggleStylesInline: Partial<IToggleStyles> = {
  label: { padding: 0 },
};

// ****** util ******
export interface ICommonStyles {
  truncateString: IRawStyle;
  twoLineTruncateString: IRawStyle;
  contentString: IRawStyle;
  pre: IRawStyle;
  code: IRawStyle;
  shimmer: IRawStyle;
  containerButton: IRawStyle;
  flexCenterVertically: IRawStyle;
}

export const CommonStyles: ICommonStyles = {
  truncateString: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  twoLineTruncateString: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    '-webkit-line-clamp': '2',
    '-webkit-box-orient': 'vertical',
  },
  contentString: {
    color: defaultSemanticColors.textSecondary,
    fontSize: 16,
  },
  pre: {
    padding: '1rem',
    backgroundColor: defaultPalette.gray300,
    color: defaultPalette.gray700,
    borderRadius: '.4rem',
  },
  code: {
    backgroundColor: defaultPalette.gray300,
    color: defaultPalette.gray800,
    borderRadius: '.25rem',
    padding: '.25rem .5rem',
  },
  shimmer: {
    backgroundImage: 'url(/shimmer.gif)',
    backgroundRepeat: 'repeat-y',
    backgroundSize: '100% 1px',
  },
  containerButton: {
    position: 'relative',
    outline: 'transparent',
    border: 0,
    backgroundColor: 'transparent',
    color: 'inherit',
    padding: 0,
    textAlign: 'inherit',
    lineHeight: 'inherit',
  },
  flexCenterVertically: {
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
  },
};
